import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SectionCard } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';

/**
 * Keep in sync with the backend import (csvParser.ts + importEmployeesFromCSV):
 * the examples are the template's own "Jane Smith" row.
 */
const TEMPLATE_COLUMNS: { name: string; needed: string; what: string; example: string }[] = [
  { name: 'name', needed: 'Required', what: 'Full name.', example: 'Jane Smith' },
  {
    name: 'email',
    needed: 'Required',
    what: 'Work email. Must be unique: it’s how existing employees are matched.',
    example: 'jane.smith@company.com',
  },
  { name: 'role', needed: 'Required', what: 'Job title.', example: 'Designer' },
  {
    name: 'department',
    needed: 'Required',
    what: 'Department name. A name that doesn’t exist yet creates the department.',
    example: 'Design',
  },
  {
    name: 'startDate',
    needed: 'New employees',
    what: 'YYYY-MM-DD or M/D/YYYY. Drives tenure-based PTO and sick hours. Required for new employees unless customAllotment is true.',
    example: '2026-07-01',
  },
  {
    name: 'customAllotment',
    needed: 'Optional',
    what: 'true (or yes, y, 1) for negotiated exceptions that keep their own PTO and sick hours. Leave blank otherwise. The import can turn this on but not off; clear it in the employee form.',
    example: 'true',
  },
  {
    name: 'ptoAllotment',
    needed: 'Custom rows',
    what: 'PTO hours, whole number. Used only when customAllotment is true or the employee is already on a custom allotment. Blank keeps the current value.',
    example: '200',
  },
  {
    name: 'sickTimeAllotment',
    needed: 'Custom rows',
    what: 'Sick hours, whole number. Used only when customAllotment is true or the employee is already on a custom allotment. Blank keeps the current value.',
    example: '56',
  },
  {
    name: 'wfhAllotment',
    needed: 'Optional',
    what: 'Work-from-home days, whole number. Blank keeps the current value (0 for new employees).',
    example: '20',
  },
];

/** A CSV header or value, set in mono on a quiet chip. */
export const ColumnName: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="mono"
    component="code"
    sx={{
      display: 'inline-block',
      px: 0.75,
      borderRadius: sbg.radius.sm,
      bgcolor: sbg.bg.surfaceSubtle,
      border: `1px solid ${sbg.border.subtle}`,
      color: sbg.text.primary,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

/** Reference table: every column the template has, what to put in it, and an example. */
export const TemplateColumnsCard: React.FC = () => (
  <SectionCard
    id="import-template-columns"
    title="Template columns"
    description="Header names must match exactly, including capitals. Column order doesn’t matter, and other columns are ignored."
    flush
  >
    <Box role="region" aria-label="Template columns" tabIndex={0} sx={{ overflowX: 'auto', '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 } }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Column</TableCell>
            <TableCell>Needed</TableCell>
            <TableCell>What to enter</TableCell>
            <TableCell>Example</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {TEMPLATE_COLUMNS.map((column) => (
            <TableRow key={column.name} sx={{ verticalAlign: 'top' }}>
              <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                <ColumnName>{column.name}</ColumnName>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', color: column.needed === 'Required' ? sbg.text.primary : sbg.text.secondary }}>
                {column.needed}
              </TableCell>
              <TableCell sx={{ minWidth: 280, maxWidth: 560, color: sbg.text.secondary }}>{column.what}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="mono" component="span" sx={{ color: sbg.text.primary }}>
                  {column.example}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  </SectionCard>
);

const IMPORT_FACTS: { title: string; body: string }[] = [
  {
    title: 'Matched by email',
    body: 'A row whose email matches an employee updates that record. Any other email adds a new employee.',
  },
  {
    title: 'Departments',
    body: 'New department names are created. Names that start with “Team” are set up as sales teams, except Team IT.',
  },
  {
    title: 'PTO and sick hours',
    body: 'Set by the tenure policy from startDate, unless customAllotment is true or the employee is already on a custom allotment. Then the file’s hours are used.',
  },
  {
    title: 'All or nothing',
    body: 'If any row has an error, nothing is imported. Fix those rows, then choose the file again.',
  },
];

/** Side card: the four rules that decide what an import changes. */
export const ImportFactsCard: React.FC = () => (
  <SectionCard id="import-facts" title="What happens on import">
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {IMPORT_FACTS.map((fact, index) => (
        <Box
          component="li"
          key={fact.title}
          sx={{
            pt: index === 0 ? 0.5 : 1.5,
            pb: index === IMPORT_FACTS.length - 1 ? 0 : 1.5,
            borderTop: index === 0 ? 0 : `1px solid ${sbg.border.subtle}`,
            display: 'grid',
            gap: 0.25,
          }}
        >
          <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
            {fact.title}
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
            {fact.body}
          </Typography>
        </Box>
      ))}
    </Box>
  </SectionCard>
);
